import { useState, useEffect } from "react";
import PhygitalIcon from "@/components/PhygitalIcon";
import { useAdmin } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  Check,
  Users,
  DollarSign,
  TrendingUp,
  Globe,
  Target,
  Zap,
  Sparkles,
  MapPin,
  BarChart3,
  Layers,
  Share,
  Scissors,
  Palette,
  PartyPopper,
  Eye,
  AlertTriangle,
  Calendar,
  Utensils,
  Dumbbell,
  GraduationCap,
  Briefcase,
  Heart,
  Image as ImageIcon,
  ArrowUpRight,
  ShoppingBag,
  Leaf,
  Repeat,
  ChevronRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCategoryPreset } from "../../lib/categoryConfig";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const ROTATING_WORDS = [
  { word: "hair", color: "from-emerald-400 to-green-400" },
  { word: "makeup", color: "from-green-400 to-teal-400" },
  { word: "event", color: "from-teal-400 to-emerald-400" },
  { word: "food", color: "from-amber-400 to-orange-400" },
  { word: "wellness", color: "from-cyan-400 to-emerald-400" }
];

const DEFAULT_SECTIONS = {
  hero: {
    title: "your preferences, perfectly communicated",
    subtitle: "the preference layer for real-world experiences",
    body: "clients save preferences once.\nhosts receive structured context before the experience begins."
  },
  problem: {
    title: "every experience starts with friction",
    consumer_points: [
      "repeating preferences every time",
      "inspo scattered across camera roll, texts, and instagram",
      "accessibility and dietary needs get lost",
      "no single place to manage everything"
    ],
    provider_points: [
      "incomplete context before appointments",
      "time lost in consultations and clarification",
      "misalignment with client expectations",
      "no structured intake after booking"
    ],
    stat: "$63B US beauty market still runs on fragmented communication"
  },
  product: {
    title: "the preference system",
    subtitle: "save once. sync everywhere. show up prepared.",
    intro: "phygital is not just a profile.\nit is a system that:",
    features: [
      "stores preferences across categories",
      "syncs them across RSVP, intake, and planning",
      "skips already-known inputs",
      "powers shareable preference cards",
      "gives providers instant, structured context"
    ],
    bottom: "clients own their preferences.\nproviders receive clarity before the experience begins."
  },
  steps: {
    title: "how it works",
    step1_title: "save",
    step1_desc: "create your preference profile across categories\nhair, makeup, events, food, and more",
    step2_title: "share",
    step2_desc: "send your preference card before your appointment or event",
    step3_title: "sync",
    step3_desc: "providers receive structured context instantly\nno repeated questions, no confusion"
  },
  surfaces: {
    title: "one system, three surfaces",
    client_points: ["personal preference hub", "inspo + planning", "reusable across experiences"],
    provider_points: ["structured intake", "client profiles", "workflow management"],
    platform_points: ["share layer that drives adoption", "synced defaults across flows", "analytics on real behavior"]
  },
  market: {
    title: "massive, multi-industry opportunity",
    subtitle: "the preference layer applies across all real-world services",
    tier1_label: "tier 1: core categories",
    tier1_desc: "beauty (hair, makeup, nails, lashes)\nhigh frequency, high preference complexity",
    tier2_label: "tier 2: expansion",
    tier2_desc: "events, culinary, instructors\ngroup coordination and structured intake",
    tier3_label: "tier 3: future",
    tier3_desc: "travel, retail, wellness, dispensary\ncross-industry preference continuity",
    bottom: "no platform currently owns structured preference context across these industries"
  },
  categories: {
    title: "11 categories across the platform",
    subtitle: "phygital starts with high-frequency, high-friction categories and expands outward",
    bottom: "each category uses the same underlying system:\npreferences - planning - sharing - sync"
  },
  city: {
    title: "controlled city rollout",
    subtitle: "phygital grows through density, not spread",
    rule: "expand only when:",
    rule_points: ["one category shows repeat usage", "host adoption is consistent", "share behavior is strong"],
    bottom: "this keeps product quality high and growth intentional"
  },
  pricing: {
    title: "free for consumers. built for providers.",
    consumer_desc: "unlimited preferences\nsharing\nplanning",
    bottom: "phygital monetizes provider workflow, not consumer access"
  },
  milestones: {
    title: "2026 milestones",
    april: ["live pilots across categories", "preference sync fully functional", "share card behavior validated"],
    may: ["subscription launch", "onboarding refinement", "first revenue"],
    june: ["category expansion", "city expansion", "team features rollout"],
    next: ["1,000+ active users", "strong category playbooks", "repeatable GTM loops"]
  },
  competitive: {
    title: "no one owns the preference layer",
    bottom: "phygital connects preferences to real-world experiences\nbefore the interaction happens"
  },
  growth_loop: {
    title: "built-in growth loop",
    bottom: "this turns every interaction into distribution"
  },
  acquirers: {
    title: "strategic acquirers",
    bottom: "phygital becomes the missing layer between discovery, booking, and the real-world experience"
  },
  cta: {
    title: "",
    subtitle: "the preference communication layer for seamless real-world experiences"
  }
};

const CATEGORY_LIST = [
  { key: "events", label: "Events", icon: PartyPopper },
  { key: "instructors", label: "Instructors", icon: GraduationCap },
  { key: "makeup", label: "Makeup", icon: Sparkles },
  { key: "hair", label: "Hair", icon: Scissors },
  { key: "nails", label: "Nails", icon: Palette },
  { key: "lashes", label: "Lashes", icon: Eye },
  { key: "culinary", label: "Culinary", icon: Utensils },
  { key: "wellness", label: "Wellness", icon: Dumbbell },
  { key: "travel", label: "Travel", icon: Globe },
  { key: "style", label: "Retail", icon: ShoppingBag },
  { key: "dispensary", label: "Dispensary", icon: Leaf }
];

// --- Compact Platform Mockups ---

const PreferenceCardMockup = ({ className = "" }) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-[220px] ${className}`}>
    <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-3 py-2 flex items-center gap-2">
      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[10px] font-bold">R</div>
      <div>
        <p className="text-white text-[10px] font-semibold">Rae's Preferences</p>
        <p className="text-white/60 text-[8px]">phyg.it/raej</p>
      </div>
    </div>
    <div className="p-2 space-y-1.5">
      <div className="bg-emerald-50 rounded-md p-2">
        <div className="flex items-center gap-1 mb-0.5">
          <Scissors className="w-2.5 h-2.5 text-emerald-600" />
          <span className="text-[9px] font-semibold text-emerald-800">Hair</span>
        </div>
        <p className="text-[8px] text-gray-600 leading-tight">Knotless braids, waist length, natural products</p>
      </div>
      <div className="bg-green-50 rounded-md p-2">
        <div className="flex items-center gap-1 mb-0.5">
          <Palette className="w-2.5 h-2.5 text-green-600" />
          <span className="text-[9px] font-semibold text-green-800">Nails</span>
        </div>
        <p className="text-[8px] text-gray-600 leading-tight">Almond, medium length, chrome finish</p>
      </div>
      {/* Inspo Grid */}
      <div className="bg-gray-50 rounded-md p-2">
        <div className="flex items-center gap-1 mb-1">
          <ImageIcon className="w-2.5 h-2.5 text-gray-500" />
          <span className="text-[9px] font-semibold text-gray-700">Inspo</span>
        </div>
        <div className="grid grid-cols-3 gap-1">
          <div className="aspect-square rounded bg-gradient-to-br from-rose-200 to-rose-300" />
          <div className="aspect-square rounded bg-gradient-to-br from-amber-200 to-amber-300" />
          <div className="aspect-square rounded bg-gradient-to-br from-emerald-200 to-emerald-300" />
        </div>
      </div>
      <button className="w-full bg-gray-900 text-white rounded-full py-1.5 text-[9px] font-medium flex items-center justify-center gap-1">
        <Share className="w-2.5 h-2.5" /> Share with Provider
      </button>
    </div>
  </div>
);

const ProviderViewMockup = ({ className = "" }) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-[220px] ${className}`}>
    <div className="bg-gray-900 px-3 py-2 flex items-center justify-between">
      <span className="text-white text-[10px] font-semibold">Provider View</span>
      <span className="text-[8px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">Synced</span>
    </div>
    <div className="p-2">
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center text-white text-[9px] font-bold">R</div>
        <div>
          <p className="text-[10px] font-semibold">Rae Johnson</p>
          <p className="text-[8px] text-gray-500">10:00 AM - Silk Press</p>
        </div>
      </div>
      <div className="space-y-1">
        {["Sensitive scalp - no heat above 350F", "Fragrance-free products only", "Prefers quiet environment"].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5 bg-gray-50 rounded-md p-1.5">
            <Check className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" />
            <span className="text-[8px] text-gray-700">{item}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 bg-emerald-50 rounded-md p-1.5">
          <ImageIcon className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" />
          <span className="text-[8px] text-emerald-700 font-medium">3 inspo photos attached</span>
        </div>
      </div>
    </div>
  </div>
);

// Client Context Mockup - shows structured client preferences for providers
const ClientContextMockup = ({ className = "" }) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-[200px] ${className}`}>
    <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-3 py-2">
      <p className="text-white text-[10px] font-semibold">Client Context</p>
      <p className="text-white/70 text-[8px]">Pre-appointment brief</p>
    </div>
    <div className="p-2 space-y-1.5">
      <div className="bg-teal-50 rounded-md p-2">
        <p className="text-[9px] font-semibold text-teal-800 mb-0.5">Accessibility</p>
        <p className="text-[8px] text-gray-600">Wheelchair accessible, quiet environment preferred</p>
      </div>
      <div className="bg-emerald-50 rounded-md p-2">
        <p className="text-[9px] font-semibold text-emerald-800 mb-0.5">Allergies</p>
        <p className="text-[8px] text-gray-600">Latex-free gloves required</p>
      </div>
      <div className="bg-green-50 rounded-md p-2">
        <p className="text-[9px] font-semibold text-green-800 mb-0.5">Communication</p>
        <p className="text-[8px] text-gray-600">Text preferred, no calls</p>
      </div>
      <div className="flex items-center gap-1 pt-1">
        <Check className="w-2.5 h-2.5 text-emerald-500" />
        <span className="text-[8px] text-emerald-600 font-medium">Synced from profile</span>
      </div>
    </div>
  </div>
);

const ShareCardMockup = ({ className = "", imageUrl = null }) => (
  <div className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-3 text-white w-[180px] shadow-lg ${className}`}>
    <div className="flex items-center gap-2 mb-2">
      <PhygitalIcon className="w-6 h-6" variant="dark" />
      <div>
        <p className="font-semibold text-[9px]">Rae's Card</p>
        <p className="text-[8px] text-gray-400">phyg.it/raej</p>
      </div>
    </div>
    {imageUrl ? (
      <img src={imageUrl} alt="Share card" className="w-full rounded-md mb-2 object-cover" />
    ) : (
      <div className="bg-white/10 rounded-md p-2 mb-2">
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-emerald-500/30 flex items-center justify-center">
              <Scissors className="w-2.5 h-2.5 text-emerald-300" />
            </div>
            <span className="text-[8px] text-gray-300">Hair - knotless braids, waist length</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-green-500/30 flex items-center justify-center">
              <Palette className="w-2.5 h-2.5 text-green-300" />
            </div>
            <span className="text-[8px] text-gray-300">Nails - almond, chrome finish</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-teal-500/30 flex items-center justify-center">
              <Heart className="w-2.5 h-2.5 text-teal-300" />
            </div>
            <span className="text-[8px] text-gray-300">Fragrance-free, quiet session</span>
          </div>
        </div>
        <div className="mt-1.5 pt-1.5 border-t border-white/10">
          <div className="flex items-center gap-1">
            <ImageIcon className="w-2.5 h-2.5 text-emerald-400" />
            <span className="text-[7px] text-emerald-300">3 inspo photos attached</span>
          </div>
        </div>
      </div>
    )}
    <div className="grid grid-cols-2 gap-1">
      <div className="bg-white/10 rounded-md py-1 text-center text-[8px]">Share</div>
      <div className="bg-white/10 rounded-md py-1 text-center text-[8px]">Open</div>
    </div>
  </div>
);

// Admin-overridable image slot
const MockupOrImage = ({ imageUrl, children, className = "", sectionKey, fieldKey, isAdmin, onImageSave }) => {
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onImageSave) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/upload/image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url?.startsWith("http") ? data.url : `${API}${data.url}`;
        onImageSave(sectionKey, fieldKey, url);
      }
    } catch (err) { console.error("Upload failed:", err); }
  };

  if (imageUrl) {
    return (
      <div className="relative group inline-block">
        <img src={imageUrl} alt="" className={`rounded-xl shadow-lg object-cover ${className}`} />
        {isAdmin && sectionKey && fieldKey && (
          <label className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors cursor-pointer rounded-xl">
            <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-3 py-1.5 rounded-lg">Replace Image</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
          </label>
        )}
      </div>
    );
  }
  if (isAdmin && sectionKey && fieldKey) {
    return (
      <label className={`flex items-center justify-center border-2 border-dashed border-emerald-400/50 rounded-xl cursor-pointer hover:bg-emerald-50/30 transition-colors ${className || 'w-full h-48'}`}>
        <div className="text-center">
          <svg className="w-8 h-8 mx-auto text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          <span className="text-xs text-emerald-500 font-medium">Upload Image</span>
        </div>
        <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </label>
    );
  }
  return children;
};

// Inline editable text for admin users
const EditableText = ({ sectionKey, fieldKey, value, isAdmin, onSave, className = "", tag = "span", multiline = false }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");

  useEffect(() => { setText(value || ""); }, [value]);

  if (!isAdmin) {
    const Tag = tag;
    return <Tag className={className}>{value}</Tag>;
  }

  if (editing) {
    return multiline ? (
      <textarea
        className={`bg-white/90 border border-emerald-400 rounded px-2 py-1 w-full min-h-[60px] outline-none text-inherit resize-y ${className}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setEditing(false); if (text !== value) onSave(sectionKey, fieldKey, text); }}
        onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setText(value || ""); } }}
        autoFocus
      />
    ) : (
      <input
        className={`bg-white/90 border border-emerald-400 rounded px-2 py-1 w-full outline-none text-inherit ${className}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setEditing(false); if (text !== value) onSave(sectionKey, fieldKey, text); }}
        onKeyDown={(e) => { if (e.key === "Enter") { setEditing(false); if (text !== value) onSave(sectionKey, fieldKey, text); } if (e.key === "Escape") { setEditing(false); setText(value || ""); } }}
        autoFocus
      />
    );
  }

  return (
    <span
      className={`cursor-pointer hover:ring-2 hover:ring-emerald-400/50 hover:ring-offset-1 rounded transition-all inline-block ${className}`}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || "(click to edit)"}
    </span>
  );
};

// --- Live Projections Slide (embedded in pitch deck) ---
const LiveProjectionsSlide = () => {
  const [projData, setProjData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/public/financial-projections`);
        if (res.ok) {
          const data = await res.json();
          if (data?.projections) setProjData(data.projections);
        }
      } catch (err) { /* ignore */ }
    };
    load();
  }, []);

  if (!projData) return null;

  const cities = projData.cities || [];
  const assumptions = projData.assumptions || {};
  const pricing = projData.pricing || {};

  const totalMarket = cities.reduce((s, c) => s + (c.total_market_size || 0), 0);
  const totalTarget = cities.reduce((s, c) => s + (c.total_target || 0), 0);
  const paying = totalTarget * (100 - (assumptions.free_percent || 70)) / 100;
  const monthlyP = paying * (100 - (assumptions.annual_percent || 30)) / 100;
  const annualP = paying * (assumptions.annual_percent || 30) / 100;
  const soloM = monthlyP * (assumptions.solo_percent || 20) / 30;
  const teamM = monthlyP * (assumptions.team_percent || 8) / 30;
  const luxeM = monthlyP * (assumptions.luxe_percent || 2) / 30;
  const soloA = annualP * (assumptions.solo_percent || 20) / 30;
  const teamA = annualP * (assumptions.team_percent || 8) / 30;
  const luxeA = annualP * (assumptions.luxe_percent || 2) / 30;
  const mrr = soloM * (pricing.solo_monthly || 11) + teamM * (pricing.team_monthly || 44) + luxeM * (pricing.luxe_monthly || 99) + (soloA * (pricing.solo_annual || 110) / 12) + (teamA * (pricing.team_annual || 440) / 12) + (luxeA * (pricing.luxe_annual || 990) / 12);
  const arr = mrr * 12;
  const fmt = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${Math.round(n)}`;
  const fmtNum = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1000) return `${Math.round(n / 1000)}K`;
    return `${Math.round(n)}`;
  };

  const phases = [
    { num: 1, label: "Phase 1 (Months 0-6)", bg: "bg-emerald-50", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
    { num: 2, label: "Phase 2 (Months 6-18)", bg: "bg-blue-50", text: "text-blue-700", badge: "bg-blue-100 text-blue-800" },
    { num: 3, label: "Phase 3 (Months 18-36)", bg: "bg-gray-50", text: "text-gray-700", badge: "bg-gray-100 text-gray-800" },
  ];

  return (
    <section className="py-16 bg-white" data-testid="slide-projections">
      <div className="w-full max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <Badge className="bg-indigo-100 text-indigo-700 mb-4">Live Projections</Badge>
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900">Revenue Projections</h2>
          <p className="text-gray-500 text-sm mt-2">Based on current city targets and pricing tiers</p>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 mb-10">
          {[
            { label: "Total Addressable", value: fmtNum(totalMarket), color: "bg-blue-50 text-blue-700 border-blue-200" },
            { label: "Target Providers", value: fmtNum(totalTarget), color: "bg-orange-50 text-orange-700 border-orange-200" },
            { label: "Est. Paying", value: fmtNum(Math.round(paying)), color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            { label: "Projected MRR", value: fmt(mrr), color: "bg-green-50 text-green-700 border-green-200" },
            { label: "Projected ARR", value: fmt(arr), color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
          ].map(m => (
            <div key={m.label} className={`rounded-xl border px-2 py-3 text-center ${m.color}`}>
              <p className="text-lg sm:text-xl md:text-2xl font-bold">{m.value}</p>
              <p className="text-[8px] sm:text-[10px] md:text-xs font-medium opacity-70 mt-0.5">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {phases.map(phase => {
            const phaseCities = cities.filter(c => c.phase === phase.num);
            if (!phaseCities.length) return null;
            return (
              <div key={phase.num} className={`rounded-xl ${phase.bg} p-4`}>
                <h3 className={`text-sm font-semibold ${phase.text} mb-3`}>{phase.label} ({phaseCities.length} cities)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {phaseCities.map(city => (
                    <div key={city.city_id} className="bg-white rounded-lg px-3 py-2 border border-gray-100 flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate" title={`${city.city}${city.state ? `, ${city.state}` : ''}`}>{city.city}{city.state ? `, ${city.state}` : ''}</p>
                        <p className="text-[10px] text-gray-400">{(city.total_target || 0).toLocaleString()} target</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// --- Main Pitch Deck Slides ---
export default function PitchDeckSlides() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [content, setContent] = useState(DEFAULT_SECTIONS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/admin/pitch-deck-slides`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.sections && Object.keys(data.sections).length > 0) {
            setContent(prev => {
              const merged = { ...prev };
              for (const key of Object.keys(data.sections)) {
                merged[key] = { ...prev[key], ...data.sections[key] };
              }
              return merged;
            });
          }
        }
      } catch (err) { /* use defaults */ }
      // Check admin status
      try {
        const meRes = await fetch(`${API}/api/me`, { headers: getHeaders() });
        if (meRes.ok) {
          const me = await meRes.json();
          if (me.role === "admin" || me.role === "super_admin" || me.is_super_admin) {
            setIsAdmin(true);
          }
        }
      } catch (err) { /* not admin */ }
    };
    load();
  }, []);

  // Save inline edit
  const handleInlineSave = async (sectionKey, fieldKey, newValue) => {
    const updatedContent = { ...content };
    updatedContent[sectionKey] = { ...updatedContent[sectionKey], [fieldKey]: newValue };
    setContent(updatedContent);
    // Persist to backend
    try {
      const currentSections = {};
      for (const key of Object.keys(updatedContent)) {
        const section = updatedContent[key];
        const defaults = DEFAULT_SECTIONS[key] || {};
        const overrides = {};
        for (const fk of Object.keys(section)) {
          if (JSON.stringify(section[fk]) !== JSON.stringify(defaults[fk])) {
            overrides[fk] = section[fk];
          }
        }
        if (Object.keys(overrides).length > 0) {
          currentSections[key] = overrides;
        }
      }
      await fetch(`${API}/api/admin/pitch-deck-slides`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ sections: currentSections })
      });
    } catch (err) { console.error("Failed to save inline edit:", err); }
  };

  const currentWord = ROTATING_WORDS[currentWordIndex];
  const s = content;

  return (
    <div className="space-y-0 w-full overflow-hidden" data-testid="pitch-deck-slides">

      {/* Admin Edit Toggle */}
      {isAdmin && (
        editMode ? (
          <div className="sticky top-0 z-50 bg-emerald-600 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-3" data-testid="admin-edit-banner">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Edit Mode. Click any text to edit, hover images to replace
            <button onClick={() => setEditMode(false)} className="ml-4 px-3 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors" data-testid="exit-edit-mode-btn">Done Editing</button>
          </div>
        ) : (
          <div className="sticky top-0 z-50 flex justify-end p-3" data-testid="admin-edit-toggle">
            <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-full shadow-lg transition-colors" data-testid="enter-edit-mode-btn">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit Deck
            </button>
          </div>
        )
      )}

      {/* ===== HERO ===== */}
      <section className="min-h-[60vh] sm:min-h-[80vh] flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 pt-10 sm:pt-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        <div className="relative text-center w-full max-w-4xl mx-auto px-4" data-testid="slide-hero">
          <PhygitalIcon className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6" variant="light" />
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            <span className="inline-flex items-center justify-center flex-wrap">
              <span>your</span>
              <span className={`inline-block mx-2 sm:mx-3 transition-all duration-500 ease-in-out bg-gradient-to-r ${currentWord.color} bg-clip-text text-transparent ${isAnimating ? 'opacity-0 scale-95 blur-sm' : 'opacity-100 scale-100 blur-0'}`}>
                {currentWord.word}
              </span>
              <span>preferences,</span>
            </span>
            <span className="block mt-1">perfectly communicated</span>
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-gray-600 mb-6" style={{overflowWrap:'break-word',wordBreak:'break-word'}}><EditableText sectionKey="hero" fieldKey="subtitle" value={s.hero.subtitle} isAdmin={isAdmin && editMode} onSave={handleInlineSave} className="text-sm sm:text-lg md:text-xl text-gray-600" /></p>
          <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto leading-relaxed" style={{overflowWrap:'break-word',wordBreak:'break-word'}}><EditableText sectionKey="hero" fieldKey="body" value={s.hero.body} isAdmin={isAdmin && editMode} onSave={handleInlineSave} multiline className="text-xs sm:text-sm text-gray-500" /></p>
          {(s.hero?.image_hero || true) && (
            <div className="mt-8 flex justify-center overflow-hidden max-w-full">
              <MockupOrImage imageUrl={s.hero?.image_hero} className="max-h-[300px] rounded-2xl shadow-xl object-cover max-w-full" sectionKey="hero" fieldKey="image_hero" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave}>
                <div className="flex gap-2 sm:gap-4 items-end scale-[0.65] sm:scale-100 origin-center">
                  <PreferenceCardMockup className="scale-90 origin-bottom" />
                  <ShareCardMockup className="scale-90 origin-bottom" />
                  <ClientContextMockup className="scale-90 origin-bottom" />
                </div>
              </MockupOrImage>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-xs sm:text-sm text-gray-500">
            <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" />Pre-Seed</span>
            <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500" />11 Categories</span>
            <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-teal-500" />2026</span>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <section className="min-h-[60vh] sm:min-h-[80vh] flex items-center py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse" />
        <div className="max-w-5xl mx-auto px-4 w-full relative z-10" data-testid="slide-problem">
          <div className="text-center mb-8">
            <Badge className="bg-red-500/20 border-red-500/30 text-red-300 mb-3"><AlertTriangle className="w-3 h-3 mr-1" /> The Problem</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold"><EditableText sectionKey="problem" fieldKey="title" value={s.problem.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white/[0.04] backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="font-bold text-emerald-300">for consumers</h3>
              </div>
              <ul className="space-y-2">
                {s.problem.consumer_points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />{pt}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/[0.04] backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-green-400" />
                </div>
                <h3 className="font-bold text-green-300">for providers</h3>
              </div>
              <ul className="space-y-2">
                {s.problem.provider_points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />{pt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 sm:px-5 py-3">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 shrink-0" />
              <span className="text-sm sm:text-base md:text-lg font-semibold">{s.problem.stat}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCT (CENTERED LAYOUT) ===== */}
      <section className="min-h-[60vh] sm:min-h-[80vh] flex items-center py-16 bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="max-w-4xl mx-auto px-4 w-full text-center" data-testid="slide-product">
          <Badge className="bg-emerald-100 text-emerald-700 mb-3">Product</Badge>
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2"><EditableText sectionKey="product" fieldKey="title" value={s.product.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          <p className="text-base text-gray-600 mb-8"><EditableText sectionKey="product" fieldKey="subtitle" value={s.product.subtitle} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></p>

          {/* Centered mockups row */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 mb-8 overflow-hidden max-w-full">
            <MockupOrImage imageUrl={s.product?.image_left} sectionKey="product" fieldKey="image_left" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave}>
              <PreferenceCardMockup />
            </MockupOrImage>
            <div className="flex sm:flex-col items-center justify-center py-2 sm:pt-12">
              <ArrowRight className="w-5 h-5 text-emerald-500 rotate-90 sm:rotate-0" />
              <span className="text-[10px] text-gray-400 mt-0 sm:mt-1 ml-1 sm:ml-0">syncs</span>
            </div>
            <MockupOrImage imageUrl={s.product?.image_right} sectionKey="product" fieldKey="image_right" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave}>
              <ProviderViewMockup />
            </MockupOrImage>
          </div>

          {/* Centered text */}
          <p className="text-gray-700 whitespace-pre-line mb-4 leading-relaxed text-sm">{s.product.intro}</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6">
            {s.product.features.map((feat, i) => (
              <span key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{feat}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500 whitespace-pre-line leading-relaxed max-w-lg mx-auto">{s.product.bottom}</p>
        </div>
      </section>

      {/* ===== HOW IT WORKS (COMPACT, CENTERED) ===== */}
      <section className="min-h-[70vh] flex items-center py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-steps">
          <div className="text-center mb-8">
            <Badge className="bg-blue-100 text-blue-700 mb-3">How It Works</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900">{s.steps.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { num: "1", title: s.steps.step1_title, desc: s.steps.step1_desc, color: "from-blue-500 to-blue-600", imgKey: "image_step1",
                mockup: <PreferenceCardMockup className="mx-auto" /> },
              { num: "2", title: s.steps.step2_title, desc: s.steps.step2_desc, color: "from-emerald-500 to-emerald-600", imgKey: "image_step2",
                mockup: <ShareCardMockup className="mx-auto" /> },
              { num: "3", title: s.steps.step3_title, desc: s.steps.step3_desc, color: "from-green-500 to-green-600", imgKey: "image_step3",
                mockup: <ProviderViewMockup className="mx-auto" /> }
            ].map((step) => (
              <div key={step.num} className="bg-white rounded-xl p-5 shadow-md border border-gray-100 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center shadow shrink-0`}>
                    <span className="text-sm font-bold text-white">{step.num}</span>
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold uppercase tracking-wide">{step.title}</h3>
                </div>
                <div className="flex justify-center mb-3">
                  <MockupOrImage imageUrl={s.steps?.[step.imgKey]} className="max-h-[240px] w-auto" sectionKey="steps" fieldKey={step.imgKey} isAdmin={isAdmin && editMode} onImageSave={handleInlineSave}>
                    <div className="transform scale-[0.9]">{step.mockup}</div>
                  </MockupOrImage>
                </div>
                <p className="text-xs text-gray-600 whitespace-pre-line leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THREE SURFACES ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-surfaces">
          <div className="text-center mb-8">
            <Badge className="bg-emerald-100 text-emerald-700 mb-3">Built for Both Sides</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900">{s.surfaces.title}</h2>
          </div>
          {(s.surfaces?.image_surfaces || true) ? (
            <div className="flex justify-center mb-6">
              <MockupOrImage imageUrl={s.surfaces?.image_surfaces} className="max-h-[400px] rounded-2xl shadow-lg object-contain" sectionKey="surfaces" fieldKey="image_surfaces" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave}>
                <div className="grid md:grid-cols-3 gap-4 w-full max-w-3xl">
                  {[
                    { label: "Clients", desc: "Save preferences, inspo, and requirements before every service", color: "bg-emerald-50 border-emerald-200" },
                    { label: "Providers", desc: "Receive preference cards, manage client profiles, and reduce intake time", color: "bg-blue-50 border-blue-200" },
                    { label: "Hosts", desc: "Collect guest preferences for events, manage RSVPs, and share with vendors", color: "bg-purple-50 border-purple-200" },
                  ].map((s, i) => (
                    <div key={i} className={`${s.color} border rounded-xl p-4 text-center`}>
                      <p className="font-semibold text-sm text-gray-800 mb-1">for {s.label}</p>
                      <p className="text-xs text-gray-600">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </MockupOrImage>
            </div>
          ) : null}
          {!s.surfaces?.image_surfaces && (
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { label: "for clients", icon: Users, points: s.surfaces.client_points, color: "bg-emerald-50 border-emerald-200", iconBg: "bg-emerald-100", iconColor: "text-emerald-600" },
              { label: "for providers", icon: Briefcase, points: s.surfaces.provider_points, color: "bg-green-50 border-green-200", iconBg: "bg-green-100", iconColor: "text-green-600" },
              { label: "for the platform", icon: Layers, points: s.surfaces.platform_points, color: "bg-teal-50 border-teal-200", iconBg: "bg-teal-100", iconColor: "text-teal-600" }
            ].map((surface) => {
              const Icon = surface.icon;
              return (
                <div key={surface.label} className={`rounded-xl p-5 border ${surface.color}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-9 h-9 rounded-lg ${surface.iconBg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${surface.iconColor}`} />
                    </div>
                    <h3 className="font-bold text-gray-900">{surface.label}</h3>
                  </div>
                  <ul className="space-y-2">
                    {surface.points.map((pt, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{pt}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* ===== MARKET ===== */}
      <section className="min-h-[70vh] flex items-center py-16 bg-gradient-to-br from-gray-900 via-emerald-900/50 to-black text-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-market">
          <div className="text-center mb-8">
            <Badge className="bg-cyan-500/20 border-cyan-500/30 text-cyan-300 mb-3">Market</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-2"><EditableText sectionKey="market" fieldKey="title" value={s.market.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
            <p className="text-gray-400 text-sm"><EditableText sectionKey="market" fieldKey="subtitle" value={s.market.subtitle} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {[
              { label: s.market.tier1_label, desc: s.market.tier1_desc, icon: Target, color: "text-cyan-400", borderColor: "border-cyan-500/20", bgColor: "bg-cyan-500/10" },
              { label: s.market.tier2_label, desc: s.market.tier2_desc, icon: TrendingUp, color: "text-emerald-400", borderColor: "border-emerald-500/20", bgColor: "bg-emerald-500/10" },
              { label: s.market.tier3_label, desc: s.market.tier3_desc, icon: Zap, color: "text-amber-400", borderColor: "border-amber-500/20", bgColor: "bg-amber-500/10" }
            ].map((tier) => {
              const Icon = tier.icon;
              return (
                <div key={tier.label} className={`bg-white/[0.04] backdrop-blur-sm rounded-xl p-5 border ${tier.borderColor}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-7 h-7 rounded-md ${tier.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-3.5 h-3.5 ${tier.color}`} />
                    </div>
                    <span className={`font-bold text-xs ${tier.color}`}>{tier.label}</span>
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">{tier.desc}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-400 text-sm">{s.market.bottom}</p>
        </div>
      </section>

      {/* ===== CATEGORIES ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-categories">
          <div className="text-center mb-8">
            <Badge className="bg-amber-100 text-amber-700 mb-3">Categories</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">{s.categories.title}</h2>
            <p className="text-gray-600 text-sm max-w-lg mx-auto">{s.categories.subtitle}</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-6 max-w-3xl mx-auto">
            {CATEGORY_LIST.map((cat) => {
              const preset = getCategoryPreset(cat.key);
              const Icon = preset?.icon || cat.icon;
              return (
                <div key={cat.key} className="bg-white rounded-lg p-3 shadow border border-gray-100 text-center hover:scale-105 transition-transform">
                  <div className="w-8 h-8 mx-auto mb-1.5 rounded-md flex items-center justify-center" style={{ backgroundColor: preset?.bgColor || '#f0fdf4' }}>
                    <Icon className="w-4 h-4" style={{ color: preset?.iconColor || '#059669' }} />
                  </div>
                  <p className="text-[10px] font-semibold text-gray-800">{cat.label}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-gray-500 whitespace-pre-line">{s.categories.bottom}</p>
        </div>
      </section>

      {/* ===== CITY STRATEGY ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 w-full" data-testid="slide-city">
          <div className="text-center mb-8">
            <Badge className="bg-orange-100 text-orange-700 mb-3">Go-To-Market</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">{s.city.title}</h2>
            <p className="text-gray-600">{s.city.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {[
              { phase: "Phase 1", cities: ["Los Angeles", "Charlotte"], color: "bg-emerald-50 border-emerald-200", dotColor: "bg-emerald-500" },
              { phase: "Phase 2", cities: ["New York", "Miami", "Atlanta"], color: "bg-blue-50 border-blue-200", dotColor: "bg-blue-500" },
              { phase: "Phase 3", cities: ["International expansion"], color: "bg-gray-100 border-gray-200", dotColor: "bg-gray-400" }
            ].map((p) => (
              <div key={p.phase} className={`rounded-xl p-4 border ${p.color}`}>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-3.5 h-3.5 text-gray-600" />
                  <span className="font-bold text-sm">{p.phase}</span>
                </div>
                <ul className="space-y-1.5">
                  {p.cities.map((c, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className={`w-1.5 h-1.5 rounded-full ${p.dotColor}`} />{c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200 max-w-2xl mx-auto">
            <p className="font-semibold text-gray-800 mb-2 text-sm">{s.city.rule}</p>
            <ul className="space-y-1.5">
              {s.city.rule_points.map((pt, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <ChevronRight className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{pt}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-3 italic">{s.city.bottom}</p>
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="min-h-[70vh] flex items-center py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-pricing">
          <div className="text-center mb-8">
            <Badge className="bg-green-100 text-green-700 mb-3">Pricing</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900"><EditableText sectionKey="pricing" fieldKey="title" value={s.pricing.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-6 max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Consumer</h3>
                  <p className="text-emerald-600 font-bold text-sm">Free Forever</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{s.pricing.consumer_desc}</p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-bold">Provider Plans</h3>
              </div>
              <div className="space-y-2">
                {[
                  { tier: "Free", price: "$0/mo", desc: "basic service setup, limited clients", bg: "bg-gray-50" },
                  { tier: "Solo", price: "$11/mo", desc: "full host workflow, unlimited clients, analytics", bg: "bg-emerald-50" },
                  { tier: "Team", price: "$44/mo", desc: "multi-user access, shared directory, permissions", bg: "bg-blue-50" },
                  { tier: "Luxe", price: "$99/mo", desc: "premium branding, advanced controls, hardware", bg: "bg-green-50" }
                ].map((plan) => (
                  <div key={plan.tier} className={`flex items-center justify-between p-2.5 rounded-lg ${plan.bg}`}>
                    <div>
                      <span className="font-bold text-xs">{plan.tier}</span>
                      <p className="text-[10px] text-gray-500">{plan.desc}</p>
                    </div>
                    <span className="font-bold text-sm text-gray-900 flex-shrink-0 ml-3">{plan.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 italic">{s.pricing.bottom}</p>
        </div>
      </section>

      {/* ===== MILESTONES ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-gradient-to-br from-gray-900 via-emerald-900/40 to-black text-white">
        <div className="max-w-4xl mx-auto px-4 w-full" data-testid="slide-milestones">
          <div className="text-center mb-8">
            <Badge className="bg-white/10 border-white/20 text-white mb-3">Roadmap</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold">{s.milestones.title}</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { month: "April", items: s.milestones.april, color: "border-emerald-500/30 bg-emerald-500/10", dotColor: "bg-emerald-400" },
              { month: "May", items: s.milestones.may, color: "border-green-500/30 bg-green-500/10", dotColor: "bg-green-400" },
              { month: "June", items: s.milestones.june, color: "border-teal-500/30 bg-teal-500/10", dotColor: "bg-teal-400" },
              { month: "Next", items: s.milestones.next, color: "border-white/10 bg-white/5", dotColor: "bg-white/40" }
            ].map((m) => (
              <div key={m.month} className={`rounded-xl p-4 border ${m.color}`}>
                <h4 className="font-bold text-white mb-2">{m.month}</h4>
                <ul className="space-y-1.5">
                  {m.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                      <div className={`w-1.5 h-1.5 rounded-full ${m.dotColor} mt-1 flex-shrink-0`} />{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMPETITIVE ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-competitive">
          <div className="text-center mb-8">
            <Badge className="bg-white/10 border-white/20 text-white mb-3">Competitive</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold"><EditableText sectionKey="competitive" fieldKey="title" value={s.competitive.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { name: "Booking platforms", owns: "own scheduling", lacks: "not preference context", icon: Calendar },
              { name: "Social platforms", owns: "own discovery", lacks: "not structured experience data", icon: Share },
              { name: "CRM tools", owns: "own communication", lacks: "not real-world preparation", icon: BarChart3 },
              { name: "Phygital", owns: "connects preferences to real-world experiences", lacks: null, icon: Zap, highlight: true }
            ].map((comp) => {
              const Icon = comp.icon;
              return (
                <div key={comp.name} className={`rounded-xl p-4 border ${comp.highlight ? 'bg-emerald-500/15 border-emerald-500/30' : 'bg-white/[0.03] border-white/10'}`}>
                  <Icon className={`w-5 h-5 mb-2 ${comp.highlight ? 'text-emerald-400' : 'text-gray-500'}`} />
                  <h4 className="font-bold text-sm mb-1">{comp.name}</h4>
                  <p className={`text-xs ${comp.highlight ? 'text-emerald-300' : 'text-gray-400'}`}>{comp.owns}</p>
                  {comp.lacks && <p className="text-xs text-red-400/70 mt-1">{comp.lacks}</p>}
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-gray-400 whitespace-pre-line">{s.competitive.bottom}</p>
          {(s.competitive?.image_competitive || isAdmin) && (
            <div className="flex justify-center mt-6">
              <MockupOrImage imageUrl={s.competitive.image_competitive} className="max-h-[300px] rounded-xl shadow-lg object-contain" sectionKey="competitive" fieldKey="image_competitive" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave} />
            </div>
          )}
        </div>
      </section>

      {/* ===== GROWTH LOOP ===== */}
      <section className="min-h-[50vh] flex items-center py-16 bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="max-w-3xl mx-auto px-4 w-full text-center" data-testid="slide-growth-loop">
          <Badge className="bg-emerald-100 text-emerald-700 mb-3">Growth</Badge>
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-6"><EditableText sectionKey="growth_loop" fieldKey="title" value={s.growth_loop.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          {s.growth_loop?.image_growth ? (
            <div className="flex justify-center mb-6">
              <MockupOrImage imageUrl={s.growth_loop.image_growth} className="max-h-[300px] rounded-xl shadow-lg object-contain" sectionKey="growth_loop" fieldKey="image_growth" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave} />
            </div>
          ) : (
            <div className="relative">
            {isAdmin && (
              <MockupOrImage imageUrl={null} className="absolute inset-0 z-10 opacity-0 hover:opacity-100 transition-opacity" sectionKey="growth_loop" fieldKey="image_growth" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave} />
            )}
            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {[
              { text: "client shares preference card", icon: Share, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
              { text: "provider experiences clarity", icon: Check, color: "bg-green-100 text-green-700 border-green-200" },
              { text: "provider adopts phygital", icon: TrendingUp, color: "bg-teal-100 text-teal-700 border-teal-200" },
              { text: "invites more clients", icon: Users, color: "bg-emerald-100 text-emerald-700 border-emerald-200" }
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className={`rounded-lg px-3 py-2 border flex items-center gap-1.5 ${step.color}`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="text-xs font-medium">{step.text}</span>
                  </div>
                  {i < 3 && <ArrowRight className="w-4 h-4 text-emerald-400 hidden md:block flex-shrink-0" />}
                </div>
              );
            })}
          </div>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center">
              <Repeat className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
            </div>
          )}
          <p className="text-gray-600 font-medium text-sm">{s.growth_loop.bottom}</p>
        </div>
      </section>

      {/* ===== ACQUIRERS ===== */}
      <section className="min-h-[60vh] flex items-center py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 w-full" data-testid="slide-acquirers">
          <div className="text-center mb-8">
            <Badge className="bg-emerald-100 text-emerald-700 mb-3">Exit Strategy</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900"><EditableText sectionKey="acquirers" fieldKey="title" value={s.acquirers.title} isAdmin={isAdmin && editMode} onSave={handleInlineSave} /></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 max-w-4xl mx-auto">
            {[
              { name: "Pinterest", points: ["visual intent", "saved preferences", "inspo-driven behavior"], icon: "P", color: "bg-red-50 border-red-200" },
              { name: "Airbnb", points: ["travel and experiences", "multi-step planning", "preference-aware coordination"], icon: "A", color: "bg-rose-50 border-rose-200" },
              { name: "Ulta Beauty", points: ["service providers", "beauty workflows", "preference-driven experiences"], icon: "U", color: "bg-purple-50 border-purple-200" },
              { name: "Meta", points: ["identity layer", "communication layer", "potential commerce integration"], icon: "M", color: "bg-blue-50 border-blue-200" }
            ].map((acq) => (
              <div key={acq.name} className={`rounded-xl p-4 border ${acq.color}`}>
                <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold text-sm mb-2">{acq.icon}</div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{acq.name}</h4>
                <ul className="space-y-1">
                  {acq.points.map((pt, i) => (
                    <li key={i} className="text-[10px] text-gray-600 flex items-start gap-1">
                      <div className="w-1 h-1 rounded-full bg-gray-400 mt-1 flex-shrink-0" />{pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500">{s.acquirers.bottom}</p>
          {(s.acquirers?.image_acquirers || isAdmin) && (
            <div className="flex justify-center mt-6">
              <MockupOrImage imageUrl={s.acquirers.image_acquirers} className="max-h-[200px] rounded-xl object-contain" sectionKey="acquirers" fieldKey="image_acquirers" isAdmin={isAdmin && editMode} onImageSave={handleInlineSave} />
            </div>
          )}
        </div>
      </section>

      {/* ===== LIVE PROJECTIONS ===== */}
      <LiveProjectionsSlide />

      {/* ===== FINAL CTA (Logo + Big Subtitle) ===== */}
      <section className="min-h-[50vh] flex items-center py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        {s.cta?.image_cta && (
          <div className="absolute inset-0">
            <img src={s.cta.image_cta} alt="" className="w-full h-full object-cover opacity-20" />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4 w-full text-center relative z-10" data-testid="slide-cta">
          <PhygitalIcon className="w-20 h-20 sm:w-28 sm:h-28 mx-auto mb-8" variant="dark" />
          <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-6 leading-tight max-w-2xl mx-auto"><EditableText sectionKey="cta" fieldKey="subtitle" value={s.cta.subtitle} isAdmin={isAdmin && editMode} onSave={handleInlineSave} multiline /></h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a href="https://phygital.systems" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors text-sm">
              <Globe className="w-4 h-4" /> phygital.systems
            </a>
            <a href="mailto:hello@phygital.systems"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-6 py-3 rounded-full font-medium hover:bg-white/20 transition-colors text-sm">
              <ArrowUpRight className="w-4 h-4" /> hello@phygital.systems
            </a>
          </div>
          <p className="text-gray-600 text-xs mt-12">2026 Phygital - Confidential</p>
        </div>
      </section>
    </div>
  );
}
